import React, { useEffect, useState, useRef, useCallback } from 'react';
import { LoadingInline } from './index';
import Modal from './modal';
import PriceHistory from './price-history';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { GameLinkRenderer, ResearchButtonRenderer } from './cell-renderers';
import { SearchResult } from '../objects';
import { researchSessionStorage } from '../services/session-storage.service.js';
import { useContext } from 'react';
import { GlobalStateContext } from '../GlobalState';
import { AiOutlineInfoCircle } from 'react-icons/ai';

/***********************************************************
 * RESEARCH COMPONENT
 ***********************************************************/

const Research = () => {
  const [searchQuery, setSearchQuery] = useState(() => researchSessionStorage.loadSearchQuery());
  const [searchResults, setSearchResults] = useState(() => researchSessionStorage.loadSearchResults());
  const [isSearching, setIsSearching] = useState(() => researchSessionStorage.loadIsSearching());
  const [selectedGame, setSelectedGame] = useState(() => researchSessionStorage.loadSelectedGame());
  const [isModalOpen, setIsModalOpen] = useState(() => researchSessionStorage.loadIsModalOpen());
  const [rowCount, setRowCount] = useState(() => researchSessionStorage.loadRowCount());

  // Get GlobalState context for row count
  const { setResearchRowCount } = useContext(GlobalStateContext);
  
  // Update GlobalState row count when local row count changes
  useEffect(() => {
    setResearchRowCount(rowCount);
  }, [rowCount, setResearchRowCount]);

  // Initialize row count when component mounts
  useEffect(() => {
    setResearchRowCount(rowCount);
  }, [setResearchRowCount, rowCount]);

  const searchTimeout = useRef(null);
  const gridRef = useRef();

  // Handle search input changes
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    researchSessionStorage.saveSearchQuery(query);

    // Clear any existing timeout
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    // Only search if query has at least 3 characters
    if (query.length >= 3) {
      setIsSearching(true);
      researchSessionStorage.saveIsSearching(true);
      
      // Debounce the search to avoid too many requests
      searchTimeout.current = setTimeout(() => {
        searchBoardGames(query);
      }, 500);
    } else {
      setSearchResults([]);
      setIsSearching(false);
      researchSessionStorage.saveIsSearching(false);
    }
  };

  // Search for board games using the BGG API
  const searchBoardGames = async (query) => {
    try {
      // Use the Netlify function to proxy the BGG search request and avoid CORS issues
      const response = await fetch(`/.netlify/functions/bgg-search?path=/boardgame&nosession=1&q=${encodeURIComponent(query)}&showcount=20`);
      const data = await response.json();
      
      // Extract the search results from the response and convert to SearchResult objects
      if (data && data.items) {
        const results = data.items.map(item => SearchResult.fromApiResponse(item));
        setSearchResults(results);
        researchSessionStorage.saveSearchResults(results);
      } else {
        setSearchResults([]);
        researchSessionStorage.saveSearchResults([]);
      }
    } catch (error) {
      console.error('Error searching for board games:', error);
      setSearchResults([]);
      researchSessionStorage.saveSearchResults([]);
    } finally {
      setIsSearching(false);
      researchSessionStorage.saveIsSearching(false);
    }
  };

  // Handle game selection (called from cell renderer via context)
  const handleGameSelect = useCallback((game) => {
    setSelectedGame(game);
    setIsModalOpen(true);
    researchSessionStorage.saveSelectedGame(game);
    researchSessionStorage.saveIsModalOpen(true);
  }, []);

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedGame(null);
    researchSessionStorage.saveIsModalOpen(false);
    researchSessionStorage.saveSelectedGame(null);
  };

  // Save state changes to session storage
  useEffect(() => {
    researchSessionStorage.saveSearchQuery(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    researchSessionStorage.saveSearchResults(searchResults);
  }, [searchResults]);

  useEffect(() => {
    researchSessionStorage.saveIsSearching(isSearching);
  }, [isSearching]);

  useEffect(() => {
    researchSessionStorage.saveSelectedGame(selectedGame);
  }, [selectedGame]);

  useEffect(() => {
    researchSessionStorage.saveIsModalOpen(isModalOpen);
  }, [isModalOpen]);

  useEffect(() => {
    researchSessionStorage.saveRowCount(rowCount);
  }, [rowCount]);

  /***********************************************************
   * AG GRID SETUP
   ***********************************************************/

  const columnDefs = [
    {
      headerName: 'Game',
      field: 'name',
      flex: 3,
      minWidth: 220,
      filter: true,
      cellRenderer: GameLinkRenderer,
      cellStyle: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        display: 'flex',
        alignItems: 'center',
      },
    },
    {
      headerName: 'Year',
      field: 'yearpublished',
      flex: 1,
      minWidth: 80,
      filter: 'agNumberColumnFilter',
      width: 90,
    },
    {
      headerName: 'Action',
      field: 'objectid',
      sortable: false,
      filter: false,
      flex: 1,
      minWidth: 160,
      cellRenderer: ResearchButtonRenderer,
    },
  ];

  const defaultColDef = {
    sortable: true,
    resizable: true,
  };

  const onGridReady = () => {
    if (gridRef.current && gridRef.current.api) {
      const count = gridRef.current.api.getDisplayedRowCount();
      setRowCount(count);
      researchSessionStorage.saveRowCount(count);
    }
  };

  const onModelUpdated = () => {
    if (gridRef.current && gridRef.current.api) {
      const count = gridRef.current.api.getDisplayedRowCount();
      setRowCount(count);
      researchSessionStorage.saveRowCount(count);
    }
  };

  return (
    <div className="app-container">
      <div className="controls-panel">
        <div className="control-group">
          <div className="control-item">
            <div className="control-input-group" style={{ position: 'relative' }}>
              <input
                id="game-search"
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Enter game name (min 3 characters)"
                className="control-input"
              />
              {searchQuery && (
                <button
                  className="clear-button"
                  onClick={() => {
                    setSearchQuery('');
                    setSearchResults([]);
                  }}
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {searchResults.length > 0 && (
        <div className="table-info">
          {isSearching && (
            <LoadingInline message="Searching" progress={0} type="spinner" />
          )}
        </div>
      )}

      <div className="table-container">
        {searchResults.length > 0 ? (
          <div className="ag-theme-alpine">
            <AgGridReact
              ref={gridRef}
              rowData={searchResults}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              rowHeight={60}
              onGridReady={onGridReady}
              onModelUpdated={onModelUpdated}
              context={{ handleGameSelect }}
              className="data-grid"
            />
          </div>
        ) : (
            <div className="no-geeklist-message">
              <p>Use the input field above to search for a board game to view its price history and market data.</p>
              <p>Click the about button <AiOutlineInfoCircle style={{fontSize: 'large'}} /> for more information.</p>
          </div>
        )}
      </div>

      {/* Price History Modal */}
      {selectedGame && (
        <Modal
          isOpen={isModalOpen}
          onClose={closeModal}
          title={selectedGame.name}
          buttonLink={`https://boardgamegeek.com/boardgame/${selectedGame.objectid}`}
        >
          <div className="game-modal-content">
            <PriceHistory 
              gameId={selectedGame.objectid} 
              showSalesTable={true}
              defaultExpanded={true}
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Research;